import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, Calculator, PiggyBank } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import EarlyRepaymentForm from './LoanEarlyRepaymentCalculatorPage/components/EarlyRepaymentForm';
import EarlyRepaymentResults from './LoanEarlyRepaymentCalculatorPage/components/EarlyRepaymentResults';
import PaymentSchedule from './LoanEarlyRepaymentCalculatorPage/components/PaymentSchedule';
import QuickSettingsSidebar from './LoanEarlyRepaymentCalculatorPage/components/QuickSettingsSidebar';
import ComparisonSidebar from './LoanEarlyRepaymentCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './LoanEarlyRepaymentCalculatorPage/components/TipsSidebar';
import InfoContent from './LoanEarlyRepaymentCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Основные параметры кредита
  loanAmount: '1000000',
  loanTerm: '5', // Years
  interestRate: '10.5', // Annual percentage
  paymentType: 'annuity', // 'annuity' or 'differentiated'
  startDate: new Date().toISOString().split('T')[0],
  currentDate: new Date().toISOString().split('T')[0],
  paymentsMade: '12', // Количество сделанных платежей

  // Параметры досрочного погашения
  earlyRepaymentAmount: '200000',
  earlyRepaymentType: 'reduction', // 'reduction' (срок) или 'payment' (платеж)
  earlyRepaymentFrequency: 'once', // 'once', 'monthly', 'quarterly', 'semi_annually', 'annually'
  earlyRepaymentStartMonth: '13', // Месяц начала досрочных платежей
  recalculateAfterEachPayment: true, // Пересчитывать график после каждого досрочного платежа
  
  // Дополнительные параметры
  includeFees: false,
  initialFee: '0', // Комиссия при оформлении кредита
  monthlyFee: '0', // Ежемесячная комиссия за обслуживание
  monthlyFeeType: 'fixed', // 'fixed' или 'percent' (от остатка)
  earlyRepaymentFee: '0', // Комиссия за досрочное погашение
  earlyRepaymentFeeType: 'fixed', // 'fixed' или 'percent' (от суммы досрочного погашения)
};

// Определение типов кредитов
export const loanTypes = [
  { id: 'consumer', name: 'Потребительский', defaultRate: 12.5 },
  { id: 'car', name: 'Автокредит', defaultRate: 9.0 },
  { id: 'mortgage', name: 'Ипотека', defaultRate: 7.5 },
  { id: 'refinance', name: 'Рефинансирование', defaultRate: 8.0 },
  { id: 'business', name: 'Бизнес-кредит', defaultRate: 14.0 },
  { id: 'secured', name: 'Под залог имущества', defaultRate: 10.0 }
];

// Определение периодов досрочного погашения
export const earlyRepaymentFrequencies = [
  { id: 'once', name: 'Разовое погашение' },
  { id: 'monthly', name: 'Ежемесячно' },
  { id: 'quarterly', name: 'Ежеквартально' },
  { id: 'semi_annually', name: 'Раз в полгода' },
  { id: 'annually', name: 'Ежегодно' }
];

// Варианты досрочного погашения
export const earlyRepaymentTypes = [
  { id: 'reduction', name: 'Уменьшение срока кредита', description: 'Размер платежа не меняется, срок кредита сокращается' },
  { id: 'payment', name: 'Уменьшение платежа', description: 'Срок не меняется, уменьшается размер ежемесячного платежа' }
];

/**
 * LoanEarlyRepaymentCalculatorPage Component - Калькулятор досрочного погашения кредита
 */
const LoanEarlyRepaymentCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('loan-calculator'), []);
  const pageTitle = 'Калькулятор досрочного погашения кредита | Gensio';
  const metaDescription = 'Калькулятор досрочного погашения кредита: расчет экономии процентов при разовом или регулярном досрочном погашении. Уменьшение срока или снижение ежемесячного платежа. Оптимальная стратегия погашения.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [originalSchedule, setOriginalSchedule] = useState([]);
  const [newSchedule, setNewSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных сценариев из localStorage ---
  useEffect(() => {
    const savedScenariosData = localStorage.getItem('savedEarlyRepaymentScenarios');
    if (savedScenariosData) {
      try {
        setSavedScenarios(JSON.parse(savedScenariosData));
      } catch (e) {
        console.error("Error loading saved scenarios:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'one-time':
          newState.earlyRepaymentFrequency = 'once';
          break;
        case 'regular':
          newState.earlyRepaymentFrequency = 'monthly';
          break;
        case 'mortgage':
          newState.loanTerm = '15';
          newState.loanAmount = '5000000';
          newState.interestRate = '7.5';
          break;
        case 'consumer':
          newState.loanTerm = '3';
          newState.loanAmount = '500000';
          newState.interestRate = '12.5';
          break;
        case 'reduce-term':
          newState.earlyRepaymentType = 'reduction';
          break;
        case 'reduce-payment':
          newState.earlyRepaymentType = 'payment';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setOriginalSchedule([]);
    setNewSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение сценария в localStorage
  const saveScenario = (name) => {
    if (!results) return;

    const newSavedScenario = {
      id: Date.now().toString(),
      name: name || `Досрочное погашение ${formData.earlyRepaymentAmount}₽`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedScenarios = [...savedScenarios, newSavedScenario];
    setSavedScenarios(updatedSavedScenarios);
    localStorage.setItem('savedEarlyRepaymentScenarios', JSON.stringify(updatedSavedScenarios));

    return newSavedScenario;
  };

  // Удаление сохраненного сценария
  const deleteSavedScenario = (id) => {
    const updatedSavedScenarios = savedScenarios.filter(scenario => scenario.id !== id);
    setSavedScenarios(updatedSavedScenarios);
    localStorage.setItem('savedEarlyRepaymentScenarios', JSON.stringify(updatedSavedScenarios));
  };

  // Загрузка сохраненного сценария
  const loadSavedScenario = (id) => {
    const scenarioToLoad = savedScenarios.find(scenario => scenario.id === id);
    if (scenarioToLoad) {
      setFormData(scenarioToLoad.formData);
      setResults(scenarioToLoad.results);
      // В реальном приложении здесь нужно также загрузить графики платежей
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <PiggyBank className="w-8 h-8 text-green-600 dark:text-green-400 flex-shrink-0" />
                Калькулятор досрочного погашения кредита
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <EarlyRepaymentForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              originalSchedule={originalSchedule}
              setOriginalSchedule={setOriginalSchedule}
              newSchedule={newSchedule}
              setNewSchedule={setNewSchedule}
              loanTypes={loanTypes}
              earlyRepaymentFrequencies={earlyRepaymentFrequencies}
              earlyRepaymentTypes={earlyRepaymentTypes}
              saveScenario={saveScenario}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <EarlyRepaymentResults results={results} />
                <PaymentSchedule 
                  originalSchedule={originalSchedule}
                  newSchedule={newSchedule}
                  results={results}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент о досрочном погашении --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickSettingsSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения сценариев --- */}
            <ComparisonSidebar 
              savedScenarios={savedScenarios}
              deleteSavedScenario={deleteSavedScenario}
              loadSavedScenario={loadSavedScenario}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Payment Schedule Table */
        .payment-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .payment-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default LoanEarlyRepaymentCalculatorPage;